import React from 'react';
import { Avatar, Box } from '@mui/material';
import { LocalOffer } from '@mui/icons-material';
import { PackPaymentChip, getPurchasedPackTitle } from '../PackBookingTooltip';
import { resolveStudioLogoUrl } from '../../utils/studioLogo';
import {
  formatDateIST,
  formatDiscountRate,
  getBookingAmount,
  getDiscountBreakdown,
  getPaymentStatus,
  getPaymentStatusStyles,
  getSourceLabel,
} from '../../utils/bookingPayment';
import { bookingStatusConfig } from '../../utils/bookingStatus';
import { SCHED } from './scheduleTheme';
import BookingStatusPill from './BookingStatusPill';

interface BookingDetailsSectionProps {
  /** The populated booking from getBookingById. */
  booking: any;
  /** Session instructor, which the booking payload doesn't carry. */
  instructorName?: string;
}

const money = (n: number) => `₹${n.toLocaleString('en-IN', { minimumFractionDigits: 0, maximumFractionDigits: 2 })}`;

const SectionTitle: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Box component="h3" sx={{ m: 0, mb: '10px', fontSize: '16px', fontWeight: 700 }}>
    {children}
  </Box>
);

const Tile: React.FC<{ label: string; value: React.ReactNode }> = ({ label, value }) => (
  <Box sx={{ p: '10px 12px', borderRadius: '12px', border: `1px solid ${SCHED.border}`, minWidth: 0 }}>
    <Box sx={{ fontSize: '12px', color: SCHED.muted }}>{label}</Box>
    <Box sx={{ fontSize: '14px', fontWeight: 600, mt: '2px', wordBreak: 'break-word' }}>{value}</Box>
  </Box>
);

const Tag: React.FC<{ children: React.ReactNode; bg?: string; color?: string; mono?: boolean }> = ({ children, bg = SCHED.hover, color = SCHED.ink, mono }) => (
  <Box
    component="span"
    sx={{
      fontSize: '12px',
      fontWeight: 700,
      p: '4px 10px',
      borderRadius: '999px',
      backgroundColor: bg,
      color,
      whiteSpace: 'nowrap',
      textTransform: mono ? 'none' : 'capitalize',
      fontFamily: mono ? 'monospace' : SCHED.font,
    }}
  >
    {children}
  </Box>
);

const Row: React.FC<{ label: React.ReactNode; value: React.ReactNode; color?: string; bold?: boolean }> = ({ label, value, color, bold }) => (
  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '12px', py: '5px' }}>
    <Box sx={{ fontSize: bold ? '16px' : '14px', fontWeight: bold ? 700 : 400, color: color || (bold ? SCHED.ink : SCHED.muted), minWidth: 0 }}>{label}</Box>
    <Box sx={{ fontSize: bold ? '16px' : '14px', fontWeight: bold ? 700 : 600, color: color || SCHED.ink, whiteSpace: 'nowrap' }}>{value}</Box>
  </Box>
);

/**
 * Everything the Booking Details page shows for one booking — ids and tags,
 * when, payment summary, studio and timeline — in the schedule's style.
 * Uses the same shared payment helpers as that page.
 */
const BookingDetailsSection: React.FC<BookingDetailsSectionProps> = ({ booking, instructorName }) => {
  const isWorkout = booking.bookingType === 'workout' || booking.bookingType === 'trial';
  const applyDateOffset = !isWorkout && booking.bookingType !== 'wellness';
  const activityDate = booking.session?.date || booking.event?.date || booking.wellnessSlotStart;
  const activityDuration = booking.session?.duration ?? booking.wellnessService?.duration;
  const instructors = [
    ...(instructorName ? [instructorName] : []),
    ...(booking.wellnessInstructors?.map((i: any) => i.name).filter(Boolean) ?? []),
    ...(booking.assignedInstructor?.name ? [booking.assignedInstructor.name] : []),
  ].filter((n, i, all) => all.indexOf(n) === i);
  const discount = getDiscountBreakdown(booking);
  const paymentStatus = getPaymentStatus(booking);
  const payStyle = getPaymentStatusStyles(paymentStatus);
  const amount = getBookingAmount(booking);
  const tx = booking.transaction;
  const statusChanged = booking.updatedAt && booking.updatedAt !== booking.createdAt;
  const statusColor = bookingStatusConfig(booking.bookingStatus).color;

  return (
    <>
      {/* Booking */}
      <Box>
        <SectionTitle>Booking</SectionTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap', mb: '12px' }}>
          <Tag mono>{booking.bookingId || booking._id}</Tag>
          <BookingStatusPill status={booking.bookingStatus} />
          <Tag>{booking.bookingType}</Tag>
          {tx?.modeOfPayment === 'subscription' && (
            <PackPaymentChip pack={tx?.purchasedPack} label="Pack Booking" sx={{ height: 24, fontSize: '0.72rem' }} />
          )}
          {(booking.isManualBooking || tx?.isManualBooking) && <Tag bg="#7c3aed" color="#FFFFFF">Manual</Tag>}
          {(booking.quantity ?? 1) > 1 && <Tag bg={SCHED.todayTint} color={SCHED.primaryText}>{booking.quantity} Tickets</Tag>}
        </Box>
        {(booking.bookingStatus === 'CHECKED_IN' || booking.bookingStatus === 'NO_SHOW') && statusChanged && (
          <Box sx={{ fontSize: '13px', color: SCHED.muted, mb: '10px' }}>
            {booking.bookingStatus === 'CHECKED_IN' ? 'Checked in' : 'No show'} {formatDateIST(booking.updatedAt, 'date', false)},{' '}
            {formatDateIST(booking.updatedAt, 'time', false)}
          </Box>
        )}
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr 1fr', sm: 'repeat(4, minmax(0, 1fr))' }, gap: '8px' }}>
          <Tile label="Date" value={formatDateIST(activityDate, 'date', applyDateOffset)} />
          <Tile label="Time" value={formatDateIST(activityDate, 'time', applyDateOffset)} />
          {activityDuration != null && <Tile label="Duration" value={`${activityDuration} min`} />}
          {instructors.length > 0 && <Tile label="Instructor" value={instructors.join(', ')} />}
        </Box>
      </Box>

      {/* Payment summary */}
      <Box>
        <SectionTitle>Payment summary</SectionTitle>
        <Box sx={{ borderRadius: '14px', border: `1px solid ${SCHED.border}`, p: '12px 16px' }}>
          <Row
            label={<>Base price{discount?.isEstimated ? <Box component="span" sx={{ fontSize: '12px' }}> (estimated)</Box> : null}</>}
            value={money(discount ? discount.originalBase : tx?.basePrice ?? 0)}
          />
          {discount && (
            <Row
              color="#e65100"
              label={
                <>
                  {getSourceLabel(discount.source)} applied
                  <Box sx={{ fontSize: '12px', color: SCHED.muted }}>
                    {discount.code && (
                      <>
                        Code <Box component="span" sx={{ fontFamily: 'monospace', fontWeight: 700 }}>{discount.code}</Box> ·{' '}
                      </>
                    )}
                    {formatDiscountRate(discount)}
                  </Box>
                </>
              }
              value={`- ${money(discount.discountAmount)}`}
            />
          )}
          <Row label="Taxes & fees" value={money(tx?.taxes ?? 0)} />
          <Box sx={{ borderTop: `1px solid ${SCHED.track}`, my: '8px' }} />
          <Row bold label={discount ? 'Amount paid' : 'Total'} value={money(amount)} />

          <Box sx={{ mt: '12px', p: '12px 14px', borderRadius: '12px', backgroundColor: payStyle.bg, border: `1px solid ${payStyle.border}`, display: 'flex', flexDirection: 'column', gap: '8px' }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '8px' }}>
              <Box sx={{ fontSize: '12px', color: SCHED.muted }}>Status</Box>
              <Tag bg="#FFFFFF" color={payStyle.color}>{paymentStatus}</Tag>
            </Box>
            {tx?.modeOfPayment === 'subscription' && tx?.purchasedPack && (
              <Box>
                <Box sx={{ fontSize: '12px', color: SCHED.muted, mb: '4px' }}>Pack used</Box>
                <PackPaymentChip pack={tx.purchasedPack} label={getPurchasedPackTitle(tx.purchasedPack)} sx={{ height: 24, fontSize: '0.72rem', maxWidth: '100%' }} />
              </Box>
            )}
            {discount && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: '4px', fontSize: '12px', fontWeight: 600, color: '#0d9488' }}>
                <LocalOffer sx={{ fontSize: 16 }} />
                {getSourceLabel(discount.source)} applied: {discount.code ?? 'N/A'}
              </Box>
            )}
            {tx?.paymentId && (
              <Box>
                <Box sx={{ fontSize: '12px', color: SCHED.muted }}>Payment ID</Box>
                <Box sx={{ fontFamily: 'monospace', fontSize: '12px', wordBreak: 'break-all' }}>{tx.paymentId}</Box>
              </Box>
            )}
            {booking.orderId && (
              <Box>
                <Box sx={{ fontSize: '12px', color: SCHED.muted }}>Order ID</Box>
                <Box sx={{ fontFamily: 'monospace', fontSize: '12px', wordBreak: 'break-all' }}>{booking.orderId}</Box>
              </Box>
            )}
            {tx?.modeOfPayment && (
              <Box>
                <Box sx={{ fontSize: '12px', color: SCHED.muted }}>Method</Box>
                <Box sx={{ fontSize: '14px', fontWeight: 600, textTransform: 'capitalize' }}>
                  {discount && tx.modeOfPayment === 'free'
                    ? `${getSourceLabel(discount.source)} Redemption`
                    : `${tx.modeOfPayment}${discount ? ` · ${getSourceLabel(discount.source)} Applied` : ''}`}
                </Box>
              </Box>
            )}
          </Box>
        </Box>
      </Box>

      {/* Studio */}
      {booking.studio && (
        <Box>
          <SectionTitle>Studio</SectionTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <Avatar src={resolveStudioLogoUrl(booking.studio)} variant="rounded" sx={{ width: 40, height: 40, borderRadius: '10px', fontFamily: SCHED.font }}>
              {booking.studio.studioName?.charAt(0).toUpperCase()}
            </Avatar>
            <Box sx={{ minWidth: 0 }}>
              <Box sx={{ fontSize: '15px', fontWeight: 600 }}>{booking.studio.studioName}</Box>
              {(booking.studio.city || booking.studio.address) && (
                <Box sx={{ fontSize: '13px', color: SCHED.muted }}>{[booking.studio.address, booking.studio.city].filter(Boolean).join(', ')}</Box>
              )}
            </Box>
          </Box>
        </Box>
      )}

      {/* Timeline */}
      <Box>
        <SectionTitle>Timeline</SectionTitle>
        <Box sx={{ position: 'relative', pl: '22px', display: 'flex', flexDirection: 'column', gap: '14px' }}>
          <Box sx={{ position: 'absolute', left: '6px', top: '8px', bottom: '8px', width: '2px', backgroundColor: SCHED.track }} />
          {[
            { label: 'Created', when: booking.createdAt, color: SCHED.primary, show: true },
            { label: `Paid ${money(amount)}`, when: null, color: '#2E7D32', show: Boolean(tx?.paymentId) },
            { label: bookingStatusConfig(booking.bookingStatus).label, when: booking.updatedAt, color: statusColor, show: Boolean(statusChanged) },
          ]
            .filter((e) => e.show)
            .map((e) => (
              <Box key={e.label} sx={{ position: 'relative' }}>
                <Box sx={{ position: 'absolute', left: '-22px', top: '3px', width: 14, height: 14, borderRadius: '50%', backgroundColor: e.color, border: '2px solid #FFFFFF', boxShadow: `0 0 0 2px ${e.color}` }} />
                <Box sx={{ fontSize: '14px', fontWeight: 600 }}>{e.label}</Box>
                {e.when && (
                  <Box sx={{ fontSize: '12px', color: SCHED.muted }}>
                    {formatDateIST(e.when, 'date', false)}, {formatDateIST(e.when, 'time', false)}
                  </Box>
                )}
              </Box>
            ))}
        </Box>
      </Box>
    </>
  );
};

export default BookingDetailsSection;
